import React from 'react';

// Shared shimmer keyframes + skeleton primitives used by every "page-shaped"
// loading state (salary, attendance, leave, profile, documents, escalations).
// Keeping this in one place means every page's loading skeleton animates and
// looks identical instead of drifting apart page by page.
export const SkeletonStyles = ({ theme }) => {
  const isDark = theme === 'dark';
  const shimmerBg = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)';
  const shimmerGrad = isDark
    ? 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0) 100%)'
    : 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.06) 50%, rgba(0,0,0,0) 100%)';

  return (
    <style>{`
      @keyframes skeletonShimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
      @keyframes skeletonPulseText {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.6; }
      }
      @keyframes skeletonSpin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      .skeleton-box {
        position: relative;
        overflow: hidden;
        background-color: ${shimmerBg};
        border-radius: 12px;
      }
      .skeleton-box::after {
        content: '';
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        background-image: ${shimmerGrad};
        animation: skeletonShimmer 2s infinite ease-in-out;
      }
    `}</style>
  );
};

export const SkeletonBox = ({ width = '100%', height = '20px', style = {}, className = '' }) => (
  <div className={`skeleton-box ${className}`} style={{ width, height, ...style }} />
);

// The pill spinner + label shown above every page skeleton
// (e.g. "Loading salary information...").
export const LoadingPill = ({ text, theme, isRTL, LoaderIcon }) => {
  const isDark = theme === 'dark';
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
        padding: '12px 24px', borderRadius: '50px',
        background: isDark ? 'rgba(15, 23, 42, 0.7)' : 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(16px)',
        border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
        boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.3)' : '0 10px 30px rgba(0,0,0,0.08)',
        width: 'fit-content', margin: '0 auto', marginBottom: '18px',
        // Many pages reuse their real header card's style for the first
        // skeleton block, which often carries a large negative marginTop
        // (to tuck the real header under the topbar). That negative margin
        // pulls the header skeleton up over this pill in normal flow. A
        // relative position + z-index guarantees the pill always paints on
        // top regardless of what a later sibling's margin does to it.
        position: 'relative', zIndex: 5,
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      {LoaderIcon
        ? <LoaderIcon size={20} color="#2e7cba" style={{ animation: 'skeletonSpin 1s linear infinite' }} />
        : (
          <div
            style={{
              width: '20px', height: '20px', borderRadius: '50%',
              border: '2.5px solid #2e7cba', borderTopColor: 'transparent',
              animation: 'skeletonSpin 1s linear infinite',
            }}
          />
        )}
      <span
        style={{
          fontWeight: '600', fontSize: '15px', color: isDark ? '#f1f5f9' : '#334155',
          animation: 'skeletonPulseText 2s infinite ease-in-out',
        }}
      >
        {text}
      </span>
    </div>
  );
};

// A single skeleton "stat" card: icon block + two text lines.
export const SkeletonStatCard = ({ cardStyle }) => (
  <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '16px' }}>
    <SkeletonBox width="56px" height="56px" style={{ borderRadius: '14px', flexShrink: 0 }} />
    <div style={{ flex: 1 }}>
      <SkeletonBox width="100%" height="26px" style={{ marginBottom: '10px' }} />
      <SkeletonBox width="60%" height="18px" />
    </div>
  </div>
);

export default SkeletonBox;
